import React from "react";
import SelectInput from "./SelectInput";
import { getTeamColors } from "../utils/badgeColors";

/**
 * Pick the teams a person belongs to.
 *
 * Chips rather than a multi-select dropdown: there are four teams, they all
 * fit on one line, and what matters most is seeing at a glance which are on.
 * A dropdown would hide exactly that behind a click.
 *
 * More than one is normal — Subash and Jayasurya are in both Support and
 * Testing — so the primary only has to be chosen when it is genuinely
 * ambiguous. With one team selected there is nothing to ask.
 *
 * Props:
 *   teams          : [{ TeamCode, TeamName }]
 *   value          : number[]          — selected team codes
 *   onChange       : (codes) => void
 *   primary        : number | ""       — the primary team code
 *   onPrimaryChange: (code) => void
 *   disabled?      : boolean
 *   note?          : string            — shown instead of the hint line
 *   error?         : string
 */
const TeamPicker = ({
  teams = [],
  value = [],
  onChange,
  primary = "",
  onPrimaryChange,
  disabled = false,
  note,
  error,
}) => {
  const selected = Array.isArray(value) ? value : [];

  const toggle = (code) => {
    if (disabled) return;

    const next = selected.includes(code)
      ? selected.filter((c) => c !== code)
      : [...selected, code];

    onChange?.(next);

    /* Keep the primary honest. Dropping the team that was primary leaves the
       choice dangling, and the server would silently pick one for you — so
       hand it to the first remaining team here, where it is visible. */
    if (!next.includes(primary)) {
      onPrimaryChange?.(next[0] ?? "");
    }
  };

  return (
    <div className="mb-3">
      <label className="form-label text-muted small fw-bold">
        Teams <span className="text-danger">*</span>
      </label>

      <div className="d-flex flex-wrap gap-2 mb-2">
        {teams.map((t) => {
          const on = selected.includes(t.TeamCode);
          const colors = getTeamColors(t.TeamName);

          return (
            <button
              key={t.TeamCode}
              type="button"
              onClick={() => toggle(t.TeamCode)}
              disabled={disabled}
              aria-pressed={on}
              style={{
                backgroundColor: on ? colors.bg : "#fff",
                color: on ? colors.text : "#6c757d",
                border: on
                  ? `1.5px solid ${colors.text}`
                  : "1.5px solid #e2e8f0",
                borderRadius: "50px",
                padding: "7px 14px",
                fontSize: "0.8rem",
                fontWeight: 600,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.6 : 1,
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.12s ease",
              }}
            >
              <i
                className={`bi ${on ? "bi-check-circle-fill" : "bi-circle"}`}
                aria-hidden="true"
              />
              {t.TeamName}
            </button>
          );
        })}
      </div>

      {/* Only worth asking once there is a real choice to make. */}
      {selected.length > 1 && !disabled && (
        <SelectInput
          width="100%"
          label="Primary team"
          name="primaryTeamCode"
          value={primary}
          onChange={(e) => onPrimaryChange?.(e.target.value)}
          options={teams
            .filter((t) => selected.includes(t.TeamCode))
            .map((t) => ({ value: t.TeamCode, label: t.TeamName }))}
          placeholder="Select primary team"
          maxHeight="140px"
        />
      )}

      <div
        className={error ? "text-danger" : "text-muted"}
        style={{ fontSize: "0.75rem" }}
      >
        {error ||
          note ||
          (selected.length > 1
            ? "They will see tasks from every team ticked. The primary team is the one their own new tasks default to."
            : "They will only see tasks belonging to the teams ticked here.")}
      </div>
    </div>
  );
};

export default TeamPicker;
